import { useInfiniteQuery } from "@tanstack/react-query"
import { Command } from "cmdk"
import { useCallback, useMemo, useState } from "react"

import useSearchState from "@hooks/useSearchState"

import { Dialog, Popover } from "@molecules"

import ObjectSelectListItem from "./ObjectSelectListItem/ObjectSelectListItem"
import { SelectContext, type SelectContextValues, useSelectSearchData } from "./Select.context"
import styles from "./Select.module.scss"
import { SelectDialogProps, SelectPopoverProps, SelectProps } from "./Select.types"
import SelectCustomValue from "./SelectCustomValue/SelectCustomValue"
import SelectEmpty from "./SelectEmpty/SelectEmpty"
import SelectError from "./SelectError/SelectError"
import SelectFetchingNextPage from "./SelectFetchingNextPage/SelectFetchingNextPage"
import SelectInput from "./SelectInput/SelectInput"
import SelectList from "./SelectList/SelectList"
import SelectListDialog from "./SelectListDialog/SelectListDialog"
import SelectListFooter from "./SelectListFooter/SelectListFooter"
import SelectListGroup from "./SelectListGroup/SelectListGroup"
import SelectListGroupDivider from "./SelectListGroupDivider/SelectListGroupDivider"
import SelectListGroupLabel from "./SelectListGroupLabel/SelectListGroupLabel"
import SelectListInput from "./SelectListInput/SelectListInput"
import SelectListItem from "./SelectListItem/SelectListItem"
import type { SelectListItem as SelectListItemType } from "./SelectListItem/SelectListItem.types"
import SelectListSearch from "./SelectListSearch/SelectListSearch"
import SelectLoading from "./SelectLoading/SelectLoading"
import SelectNoResults from "./SelectNoResults/SelectNoResults"
import SelectViewport from "./SelectViewport/SelectViewport"
import UserSelectListItem from "./UserSelectListItem/UserSelectListItem"

interface SelectSearchPage<T = unknown> {
    results: T[]
    next: string | null
}

function buildSearchEndpoint(endpoint: string, debouncedSearchTerm: string, searchParamName: string = "keywords") {
    if (!endpoint) {
        return ""
    }

    const [path, queryString = ""] = endpoint.split("?")
    const params = new URLSearchParams(queryString)

    if (debouncedSearchTerm) {
        params.set(searchParamName, debouncedSearchTerm)
    }

    const nextQueryString = params.toString()
    return nextQueryString ? `${path}?${nextQueryString}` : path
}

function normalizeSearchPage<T>(json: unknown): SelectSearchPage<T> {
    if (Array.isArray(json)) {
        return {
            results: json as T[],
            next: null,
        }
    }

    if (json && typeof json === "object") {
        const page = json as Record<string, unknown>

        if (!Array.isArray(page.results)) {
            throw new TypeError(
                `normalizeSearchPage: expected "results" to be an array, got ${JSON.stringify(page.results)}`,
            )
        }

        if ("next" in page && page.next !== null && typeof page.next !== "string") {
            throw new TypeError(
                `normalizeSearchPage: expected "next" to be a string or null, got ${JSON.stringify(page.next)}`,
            )
        }

        return {
            results: page.results as T[],
            next: typeof page.next === "string" ? page.next : null,
        }
    }

    throw new TypeError(`normalizeSearchPage: expected an array or object response, got ${JSON.stringify(json)}`)
}

function Select(props: SelectProps) {
    const {
        children,
        mode = "popover",
        selectionMode = "single",
        defaultSelectedItem = null,
        syncSubtextToSuffix = true,
        defaultCustomValue = "",
        isOpen,
        onOpenChange,
        shouldFilter = true,
        searchMode = "client",
        searchEndpoint = "",
        searchData: externalSearchData,
    } = props
    const [selectedItem, setSelectedItem] = useState<SelectListItemType | null>(defaultSelectedItem)

    const [customValue, setCustomValue] = useState<string>(defaultCustomValue)

    // Maintain internal open state so context.setIsOpen always works,
    // even when the consumer doesn't pass isOpen/onOpenChange.
    const [internalOpen, setInternalOpen] = useState(false)
    const effectiveOpen = isOpen ?? internalOpen
    const handleOpenChange = useCallback(
        (open: boolean) => {
            setInternalOpen(open)
            onOpenChange?.(open)
        },
        [onOpenChange],
    )

    const searchState = useSearchState({
        resetOnChange: effectiveOpen,
    })

    const isServerSearchEnabled = searchMode === "server" && searchEndpoint !== ""

    const {
        data: searchQueryData,
        isLoading: isSearchLoading,
        isFetching: isSearchFetching,
        isFetchingNextPage,
        isError: isSearchError,
        fetchNextPage,
        hasNextPage,
    } = useInfiniteQuery<SelectSearchPage>({
        queryKey: ["selectSearch", searchEndpoint, searchState.debouncedSearchTerm],
        queryFn: async ({ pageParam }) => {
            const nextPageUrl = typeof pageParam === "string" ? pageParam : ""
            const requestUrl = nextPageUrl || buildSearchEndpoint(searchEndpoint, searchState.debouncedSearchTerm)

            if (!requestUrl) {
                return {
                    results: [],
                    next: null,
                }
            }

            const response = await fetch(requestUrl)

            if (!response.ok) {
                throw new Error("Failed to fetch select data")
            }

            return normalizeSearchPage(await response.json())
        },
        initialPageParam: "",
        getNextPageParam: (lastPage) => lastPage.next ?? undefined,
        enabled: isServerSearchEnabled && effectiveOpen,
        refetchOnMount: "always",
    })

    const searchResults = useMemo(
        () => searchQueryData?.pages.flatMap((page) => page.results ?? []) ?? [],
        [searchQueryData],
    )

    const searchData = useMemo<SelectContextValues["searchData"]>(() => {
        if (searchMode === "external") {
            return externalSearchData
        } else if (searchMode === "server") {
            return {
                results: searchResults,
                isLoading: isSearchLoading,
                isFetching: isSearchFetching,
                isFetchingNextPage,
                isError: isSearchError,
                fetchNextPage: fetchNextPage as () => Promise<unknown>,
                hasNextPage: Boolean(hasNextPage),
                searchState,
            }
        } else {
            return undefined
        }
    }, [
        searchMode,
        externalSearchData,
        fetchNextPage,
        hasNextPage,
        isSearchError,
        isSearchFetching,
        isSearchLoading,
        isFetchingNextPage,
        searchResults,
        searchState,
    ])

    const contextValue: SelectContextValues = {
        mode,
        selectionMode,
        selectedItem,
        setSelectedItem,
        syncSubtextToSuffix,
        customValue,
        setCustomValue,
        isOpen: effectiveOpen,
        setIsOpen: handleOpenChange,
        searchData,
    }

    if (mode === "dialog") {
        const dialogProps = props as SelectDialogProps
        const { variant = "default" } = dialogProps
        return (
            <Command className={styles.base} shouldFilter={shouldFilter}>
                <SelectContext.Provider value={contextValue}>
                    <Dialog.Root
                        isOpen={effectiveOpen}
                        onOpenChange={handleOpenChange}
                        mode="dialog"
                        variant={variant}
                    >
                        {children}
                    </Dialog.Root>
                </SelectContext.Provider>
            </Command>
        )
    } else {
        const popoverProps = props as SelectPopoverProps
        const { isAnchored, mobilePresentation } = popoverProps
        return (
            <Command className={styles.base} shouldFilter={shouldFilter}>
                <SelectContext.Provider value={contextValue}>
                    <Popover.Root
                        isAnchored={isAnchored}
                        mobilePresentation={mobilePresentation}
                        isOpen={effectiveOpen}
                        onOpenChange={handleOpenChange}
                    >
                        {children}
                    </Popover.Root>
                </SelectContext.Provider>
            </Command>
        )
    }
}

const Root = Select
const Viewport = SelectViewport
const List = SelectList
const Trigger = Popover.Trigger
const DialogList = SelectListDialog
const ListFooter = SelectListFooter
const Input = SelectInput
const ListInput = SelectListInput
const ListSearch = SelectListSearch
const ListItem = SelectListItem
const ListGroupLabel = SelectListGroupLabel
const ObjectListItem = ObjectSelectListItem
const UserListItem = UserSelectListItem
const ListGroup = SelectListGroup
const ListGroupDivider = SelectListGroupDivider
const CustomValue = SelectCustomValue
const Empty = SelectEmpty
const NoResults = SelectNoResults
const Loading = SelectLoading
const FetchingNextPage = SelectFetchingNextPage
const useSearchData = useSelectSearchData

export default {
    Root,
    Viewport,
    List,
    Trigger,
    Dialog: DialogList,
    ListFooter,
    Input,
    ListInput,
    ListSearch,
    ListItem,
    ListGroupLabel,
    ObjectListItem,
    UserListItem,
    ListGroup,
    ListGroupDivider,
    CustomValue,
    Empty,
    NoResults,
    Loading,
    FetchingNextPage,
    Error: SelectError,
    useSearchData,
}
